import { useTranslation } from "react-i18next";
import { AdminOrgDto, AdminUpdateOrganizationRequest } from "../../../autogen/bff-api";

export type OrgAccessFlagKey = Exclude<keyof AdminUpdateOrganizationRequest, "name">;

export interface OrgAccessFlag {
  key: OrgAccessFlagKey;
  label: string;
}

export interface OrgAccessGroup {
  title: string;
  chipBg: string;
  chipFg: string;
  flags: OrgAccessFlag[];
}

export const useOrgAccessGroups = (): OrgAccessGroup[] => {
  const { t } = useTranslation();
  return [
    {
      title: t("Modules"),
      chipBg: "smChipTealBg",
      chipFg: "smChipTealFg",
      flags: [
        { key: "hasBuyerAccess", label: t("Buyer") },
        { key: "hasContractAccess", label: t("Contracts") },
        { key: "hasProjectAccess", label: t("Projects") },
        { key: "hasProductAccess", label: t("Products") },
      ],
    },
    {
      title: t("Sourcing"),
      chipBg: "smChipGreenBg",
      chipFg: "smChipGreenFg",
      flags: [
        { key: "hasSourcingEventV2Access", label: t("Public procurement (V2)") },
        { key: "hasNegotiationAccess", label: t("Negotiations") },
        { key: "canSeeDeliveredBidsBeforeDeadline", label: t("See delivered bids before deadline") },
      ],
    },
    {
      title: t("Signing"),
      chipBg: "smChipAmberBg",
      chipFg: "smChipAmberFg",
      flags: [
        { key: "hasSignatureAccess", label: t("E-signing") },
        { key: "hasBankIdAccess", label: t("BankID") },
      ],
    },
    {
      title: t("AI & Fency"),
      chipBg: "smChipVioletBg",
      chipFg: "smChipVioletFg",
      flags: [
        { key: "hasAiAccess", label: t("AI features") },
        { key: "hasAssistantAccess", label: t("SourceMagnet assistant") },
        { key: "hasFencyContractSearchAccess", label: t("Fency contract search") },
        { key: "fencySyncEnabled", label: t("Fency sync") },
      ],
    },
    {
      title: t("Other"),
      chipBg: "smChipBlueBg",
      chipFg: "smChipBlueFg",
      flags: [
        { key: "hasDocumentTemplateAccess", label: t("Document templates") },
        { key: "hasIFRSAccess", label: t("IFRS") },
        { key: "hasOrganizationAssessmentAccess", label: t("Organization assessment") },
      ],
    },
  ];
};

export const getOrgAccessValues = (org: AdminOrgDto): Record<OrgAccessFlagKey, boolean> => ({
  hasBuyerAccess: org.subscription.hasBuyerAccess,
  hasContractAccess: org.subscription.hasContractAccess,
  hasProjectAccess: org.subscription.hasProjectAccess,
  hasProductAccess: org.subscription.hasProductAccess,
  hasSourcingEventV2Access: org.subscription.hasSourcingEventV2Access,
  hasNegotiationAccess: org.subscription.hasNegotiationAccess,
  canSeeDeliveredBidsBeforeDeadline: org.canSeeDeliveredBidsBeforeDeadline,
  hasSignatureAccess: org.subscription.hasSignatureAccess,
  hasBankIdAccess: org.subscription.hasBankIdAccess,
  hasAiAccess: org.subscription.hasAiAccess,
  hasAssistantAccess: org.subscription.hasAssistantAccess,
  hasFencyContractSearchAccess: org.subscription.hasFencyContractSearchAccess,
  fencySyncEnabled: org.subscription.fencySyncEnabled,
  hasDocumentTemplateAccess: org.subscription.hasDocumentTemplateAccess,
  hasIFRSAccess: org.subscription.hasIFRSAccess,
  hasOrganizationAssessmentAccess: org.subscription.hasOrganizationAssessmentAccess,
});
